"use strict";
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetHostPathFinder = void 0;
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const lodash = require("lodash");
const os = require("os");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const FileUtilities_1 = require("../Utils/FileUtilities");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const DotnetResolver_1 = require("./DotnetResolver");
const InstallTrackerSingleton_1 = require("./InstallTrackerSingleton");
const RegistryReader_1 = require("./RegistryReader");
class DotnetHostPathFinder {
    constructor(workerContext, utilityContext, executor, file, resolver) {
        var _a, _b, _c;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        this.file = file;
        this.resolver = resolver;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
        (_b = this.file) !== null && _b !== void 0 ? _b : (this.file = new FileUtilities_1.FileUtilities());
        (_c = this.resolver) !== null && _c !== void 0 ? _c : (this.resolver = new DotnetResolver_1.DotnetResolver(workerContext, utilityContext, this.executor));
    }
    /**
     *
     * @returns The DOTNET_ROOT environment variable, which is the root path for the dotnet installation.
     * Some applications, such as `dotnet test`, prefer DOTNET_ROOT over the PATH setting.
     * DOTNET_ROOT is also not the only setting.
     * DOTNET_ROOT(x86) - Deprecated. Only used when running 32-bit executables. VS Code 32 bit is deprecated, so don't support this.
     * DOTNET_ROOT_X86 - The non deprecated version of the above variable, still ignore.
     * DOTNET_ROOT_X64 - Used when running 64-bit executables on an ARM64 OS.
     * Node only runs on x64 and not ARM, but that doesn't mean the .NET Application won't run on ARM.
     *
     * DOTNET_HOST_PATH may be set but this is owned by the host. Don't respect any user setting here.
     *
     * The VS Code Workspace environment may also be different from the System environment.
     */
    findDotnetRootPath(suggestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupRootPATH(`Looking up .NET on the root.`));
            if (suggestedArchitecture === 'x64' && (this.executor !== undefined ? (yield (0, TypescriptUtilities_1.getOSArch)(this.executor)).includes('arm') : false)) {
                let dotnetOnRootEmulationPath = process.env.DOTNET_ROOT_X64;
                if ((0, TypescriptUtilities_1.EnvironmentVariableIsDefined)(dotnetOnRootEmulationPath)) {
                    // DOTNET_ROOT should be set to the directory containing the dotnet executable, not the executable itself.
                    // https://learn.microsoft.com/en-us/dotnet/core/tools/dotnet-environment-variables
                    dotnetOnRootEmulationPath = path.join(dotnetOnRootEmulationPath, (0, TypescriptUtilities_1.getDotnetExecutable)());
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootEmulationPATHFound(`Under emulation and emulation root is set to ${dotnetOnRootEmulationPath}.`));
                    return dotnetOnRootEmulationPath;
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootUnderEmulationButNoneSet(`Under emulation but DOTNET_ROOT_X64 is not set.`));
                }
            }
            let dotnetOnRootPath = process.env.DOTNET_ROOT;
            if ((0, TypescriptUtilities_1.EnvironmentVariableIsDefined)(dotnetOnRootPath)) {
                // DOTNET_ROOT should be set to the directory containing the dotnet executable, not the executable itself.
                dotnetOnRootPath = path.join(dotnetOnRootPath, (0, TypescriptUtilities_1.getDotnetExecutable)());
                this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRootPATHFound(`Found .NET on the root: ${dotnetOnRootPath}`));
                return dotnetOnRootPath;
            }
            return undefined;
        });
    }
    findExtensionManagedRuntimes() {
        return __awaiter(this, void 0, void 0, function* () {
            const installedVersions = yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(this.workerContext.eventStream, this.workerContext.extensionState).getExistingInstalls(this.workerContext.installDirectoryProvider);
            const installedVersionRuntimeHostPaths = installedVersions.map(install => ({ installRecord: install, path: path.join(this.workerContext.installDirectoryProvider.getInstallDir(install.dotnetInstall.installId), (0, TypescriptUtilities_1.getDotnetExecutable)()) }));
            return installedVersionRuntimeHostPaths !== null && installedVersionRuntimeHostPaths !== void 0 ? installedVersionRuntimeHostPaths : [];
        });
    }
    /**
     * @remarks This only checks dotnet --list-runtimes or --list-sdks, so it can be more performant for the base case.
     * This allows skipping which, where, and also does not rely on --info which is slower because it shells to the SDK instead of just being the host.
     * @param suggestedArchitecture - The architecture to suggest to the resolver, or null if not applicable - must be validated.
     * @returns The path to the dotnet executable, which may be a symlink to the actual executable.
     */
    findDotnetFastFromListOnly(suggestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalPath = yield this.resolver.resolveTruePath('dotnet', suggestedArchitecture);
            return finalPath;
        });
    }
    /**
     *
     * @returns A set of the path environment variable(s) for which or where dotnet, which may need to be converted to the actual path if it points to a polymorphic executable.
     * For example, `snap` installs dotnet to snap/bin/dotnet, which you can call --list-runtimes on.
     * The 'realpath' of that is 'usr/bin/snap', which you cannot invoke --list-runtimes on, because it is snap.
     * In this case, we need to use this polymorphic path to find the actual path later.
     *
     * In an install such as homebrew, the PATH is not indicative of all of the PATHs. So dotnet may be missing in the PATH even though it is found in an alternative shell.
     * The PATH can be discovered using path_helper on mac.
     */
    findRawPathEnvironmentSetting() {
        return __awaiter(this, arguments, void 0, function* (tryUseTrueShell = true, suggestedArchitecture) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            const searchEnvironment = process.env; // this is the default, but sometimes it does not get picked up
            const options = tryUseTrueShell && os.platform() !== 'win32' ?
                {
                    // Use /bin/bash as the default shell, but if it's not available try to use /bin/sh
                    // Users may see different behavior due to this if they have a custom shell such as zsh but those shells may have unexpected behaviors.
                    env: searchEnvironment, shell: process.env.SHELL === '/bin/bash' ? '/bin/bash' : '/bin/sh', dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS
                }
                :
                    {
                        env: searchEnvironment, dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS
                    };
            // Log the PATH for diagnostic debugging via logs.
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Looking up .NET on the path. Process.env.path: ${process.env.PATH}`));
            if (os.platform() === 'win32') {
                (_a = this.executor) === null || _a === void 0 ? void 0 : _a.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['%PATH%']), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Win): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                }).catch(() => { });
            }
            else {
                (_b = this.executor) === null || _b === void 0 ? void 0 : _b.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['$PATH']), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                    // Log the default shell state
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Unix-Default-Shell): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                }).catch(() => { });
                if (!(options.shell === '/bin/bash')) {
                    (_c = this.executor) === null || _c === void 0 ? void 0 : _c.execute(CommandExecutor_1.CommandExecutor.makeCommand('echo', ['$PATH']), { shell: 'bin/bash', dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYS_CMD_SEARCH_CACHE_DURATION_MS }, false).then((result) => {
                        this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Execution Path (Unix Bash): ${result === null || result === void 0 ? void 0 : result.stdout}`));
                    }).catch(() => { });
                }
            }
            let pathLocatorCommand = '';
            if (os.platform() === 'win32') {
                pathLocatorCommand = (_f = (_e = (yield ((_d = this.executor) === null || _d === void 0 ? void 0 : _d.tryFindWorkingCommand([
                    // We have to give the command an argument to return status 0, and the only thing its guaranteed to find is itself :)
                    CommandExecutor_1.CommandExecutor.makeCommand('where', ['where']),
                    CommandExecutor_1.CommandExecutor.makeCommand('where.exe', ['where.exe']),
                    CommandExecutor_1.CommandExecutor.makeCommand('%SystemRoot%\\System32\\where.exe', ['%SystemRoot%\\System32\\where.exe']), // if PATH is corrupted
                    CommandExecutor_1.CommandExecutor.makeCommand('C:\\Windows\\System32\\where.exe', ['C:\\Windows\\System32\\where.exe']) // in case SystemRoot is corrupted, best effort guess
                ], options)))) === null || _e === void 0 ? void 0 : _e.commandRoot) !== null && _f !== void 0 ? _f : 'where';
            }
            else {
                pathLocatorCommand = (_j = (_h = (yield ((_g = this.executor) === null || _g === void 0 ? void 0 : _g.tryFindWorkingCommand([
                    CommandExecutor_1.CommandExecutor.makeCommand('which', ['which']),
                    CommandExecutor_1.CommandExecutor.makeCommand('/usr/bin/which', ['/usr/bin/which']), // if PATH is corrupted
                ], options)))) === null || _h === void 0 ? void 0 : _h.commandRoot) !== null && _j !== void 0 ? _j : 'which';
            }
            options.dotnetInstallToolCacheTtlMs = CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS;
            const findCommand = CommandExecutor_1.CommandExecutor.makeCommand(pathLocatorCommand, ['dotnet']);
            const dotnetsOnPATH = (_l = (yield ((_k = this.executor) === null || _k === void 0 ? void 0 : _k.execute(findCommand, options, false)))) === null || _l === void 0 ? void 0 : _l.stdout.split('\n').map(x => x.trim()).filter(x => x !== '');
            if (dotnetsOnPATH && ((_m = dotnetsOnPATH === null || dotnetsOnPATH === void 0 ? void 0 : dotnetsOnPATH.length) !== null && _m !== void 0 ? _m : 0) > 0) {
                this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathPATHFound(`Found.NET on the path: ${JSON.stringify(dotnetsOnPATH)}`));
                return (_o = this.resolver) === null || _o === void 0 ? void 0 : _o.resolveTruePaths(dotnetsOnPATH, suggestedArchitecture);
            }
            else {
                const pathsOnPATH = (_p = process.env.PATH) === null || _p === void 0 ? void 0 : _p.split((0, TypescriptUtilities_1.getPathSeparator)());
                const validPathsOnPATH = [];
                if (pathsOnPATH && ((_q = pathsOnPATH === null || pathsOnPATH === void 0 ? void 0 : pathsOnPATH.length) !== null && _q !== void 0 ? _q : 0) > 0) {
                    const dotnetExecutable = (0, TypescriptUtilities_1.getDotnetExecutable)();
                    for (const pathOnPATH of pathsOnPATH) {
                        const resolvedDotnetPath = path.join(pathOnPATH, dotnetExecutable);
                        if (yield this.file.exists(resolvedDotnetPath)) {
                            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupPATH(`Looking up.NET on the path by processing PATH string.resolved: ${resolvedDotnetPath}.`));
                            validPathsOnPATH.push(resolvedDotnetPath);
                        }
                    }
                }
                if (((_r = validPathsOnPATH === null || validPathsOnPATH === void 0 ? void 0 : validPathsOnPATH.length) !== null && _r !== void 0 ? _r : 0) > 0) {
                    return validPathsOnPATH;
                }
            }
            return undefined;
        });
    }
    /**
     * @returns The 'realpath' or resolved path for dotnet from which or where dotnet.
     * Some installers, such as the Ubuntu install with the PMC Feed, the PATH is set to /usr/bin/dotnet which is a symlink to /usr/share/dotnet.
     * If we want to return the actual path, we need to use realpath.
     *
     * We can't use realpath on all paths, because some paths are polymorphic executables and the realpath is invalid.
     */
    findRealPathEnvironmentSetting() {
        return __awaiter(this, arguments, void 0, function* (tryUseTrueShell = true, suggestedArchitecture) {
            var _a, _b, _c;
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathLookupRealPATH(`Looking up.NET on the real path.`));
            const dotnetsOnPATH = yield this.findRawPathEnvironmentSetting(tryUseTrueShell, suggestedArchitecture);
            const realPaths = [];
            for (const dotnetOnPATH of dotnetsOnPATH !== null && dotnetsOnPATH !== void 0 ? dotnetsOnPATH : []) {
                const resolvedPath = yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(dotnetOnPATH));
                if (resolvedPath) {
                    realPaths.push(resolvedPath);
                }
            }
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathRealPATHFound(`Found.NET on the path: ${JSON.stringify(dotnetsOnPATH)}, realpath: ${realPaths}`));
            if (((_b = realPaths === null || realPaths === void 0 ? void 0 : realPaths.length) !== null && _b !== void 0 ? _b : 0) === 0) {
                return undefined;
            }
            return (_c = this.resolver) === null || _c === void 0 ? void 0 : _c.resolveTruePaths(realPaths, suggestedArchitecture);
        });
    }
    findHostInstallPaths(requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathHostFxrResolutionLookup(`Looking up .NET without checking the PATH.`));
            if (os.platform() === 'win32') {
                const registryReader = new RegistryReader_1.RegistryReader(this.workerContext, this.utilityContext, this.executor);
                const hostPathWin = yield registryReader.getHostLocation(requestedArchitecture);
                const realHostPathWin = hostPathWin ? yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(hostPathWin)) : null;
                const paths = [];
                if (hostPathWin) {
                    paths.push(path.join(hostPathWin, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                }
                if (realHostPathWin) {
                    paths.push(path.join(realHostPathWin, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                }
                if (paths.length > 0) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathOnRegistry(`The host could be found in the registry.${JSON.stringify(paths)}`));
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoHostOnRegistry(`The host could not be found in the registry`));
                }
                return (_b = this.resolver) === null || _b === void 0 ? void 0 : _b.resolveTruePaths(lodash.uniq(paths), requestedArchitecture);
            }
            else {
                // Possible values for arch are: x86, x64, arm32, arm64
                // x86 and arm32 are not a concern since 32 bit vscode is out of support and not needed by other extensions
                // https://github.com/dotnet/designs/blob/main/accepted/2021/install-location-per-architecture.md#new-format
                const paths = [];
                const netSixAndAboveHostInstallSaveLocation = `/etc/dotnet/install_location_${requestedArchitecture}`;
                const netFiveAndNetSixAboveFallBackInstallSaveLocation = `/etc/dotnet/install_location`;
                paths.push(...(yield this.getPathsFromEtc(netSixAndAboveHostInstallSaveLocation)));
                paths.push(...(yield this.getPathsFromEtc(netFiveAndNetSixAboveFallBackInstallSaveLocation)));
                if (paths.length > 0) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathOnFileSystem(`The host could be found in the file system.${JSON.stringify(paths)}`));
                }
                else {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoHostOnFileSystem(`The host could not be found in the file system.`));
                }
                return (_c = this.resolver) === null || _c === void 0 ? void 0 : _c.resolveTruePaths(lodash.uniq(paths), requestedArchitecture);
            }
        });
    }
    getPathsFromEtc(etcLoc) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const paths = [];
            if (yield this.file.exists(etcLoc)) {
                try {
                    const installPath = (yield this.file.read(etcLoc)).trim();
                    paths.push(path.join(installPath, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                    const realPath = yield ((_a = this.file) === null || _a === void 0 ? void 0 : _a.realpath(installPath));
                    if (realPath) {
                        paths.push(path.join(realPath, (0, TypescriptUtilities_1.getDotnetExecutable)()));
                    }
                }
                catch (error) // eslint-disable-line @typescript-eslint/no-explicit-any
                 {
                    // readfile throws if the file gets deleted in between the existing check and now
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    this.workerContext.eventStream.post(new EventStreamEvents_1.FileDoesNotExist(`The host was deleted after checking in the file system.${error === null || error === void 0 ? void 0 : error.message} ${etcLoc}`));
                }
            }
            else {
                this.workerContext.eventStream.post(new EventStreamEvents_1.FileDoesNotExist(`The host save location never existed at ${etcLoc}`));
            }
            return paths;
        });
    }
}
exports.DotnetHostPathFinder = DotnetHostPathFinder;
//# sourceMappingURL=DotnetHostPathFinder.js.map